// Hook for the receipts-attention summary (slice 4g). Fetches the
// org-wide `/api/mfg/receipts/attention` payload and exposes:
//
//   - `count`            -- headline "needs attention" number (drives
//                            the Inventory banner)
//   - `itemsByReceiptId` -- O(1) lookup so the Receipts-tab list can
//                            render a per-row status chip without
//                            running its own classification logic
//   - `refresh()`        -- callable after Post / Dismiss / Re-extract
//                            so banner + chips update without waiting
//                            for the next mount
//
// Kept lean -- no SWR, no cross-mount caching. Each Inventory-panel
// mount triggers one fetch; refresh() is the only refetch path.
import { useCallback, useEffect, useMemo, useState } from "react";
import { listManufacturingReceiptsAttention } from "@/lib/manufacturingClient";
import type { MfgReceiptAttentionItem } from "@/lib/manufacturing/types";

export type UseManufacturingReceiptsAttention = {
  loading: boolean;
  error: string | null;
  count: number;
  items: MfgReceiptAttentionItem[];
  itemsByReceiptId: Map<string, MfgReceiptAttentionItem>;
  refresh: () => Promise<void>;
};

export function useManufacturingReceiptsAttention(opts: {
  enabled: boolean;
  limit?: number;
}): UseManufacturingReceiptsAttention {
  const { enabled, limit } = opts;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<MfgReceiptAttentionItem[]>([]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    const r = await listManufacturingReceiptsAttention({ limit });
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setCount(r.data.needs_attention_count);
    setItems(r.data.items);
  }, [enabled, limit]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const itemsByReceiptId = useMemo(() => {
    const m = new Map<string, MfgReceiptAttentionItem>();
    for (const it of items) m.set(it.receipt_id, it);
    return m;
  }, [items]);

  return { loading, error, count, items, itemsByReceiptId, refresh };
}
